import { LOS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { LearningObjectiveStatementStatus } from '@exam-management/graphql';
import {
  DeleteOutlineIcon,
  DoDisturbAltOutlinedIcon,
  EditOutlinedIcon,
  MoreMenuIconButton,
  MoreVertIcon,
  Text,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { usePermission } from '@shared/permissions';
import { useMemo } from 'react';

import { DELETE_LOS_REQUIRED_PERMISSIONS, EDIT_LOS_REQUIRED_PERMISSIONS } from '../../constants';

export interface LOSListRowActionMenuProps {
  losId: string;
  status: LearningObjectiveStatementStatus;
  questionCount?: number;
  onEdit?: (losId: string) => void;
  onDelete?: (losId: string) => void;
  onDisable?: (losId: string) => void;
}

interface LOSListRowActionMenuOption extends MoreMenuIconButtonOption {
  requiredPermission: string[];
  requiredStatuses?: LearningObjectiveStatementStatus[];
}

export default function LOSListRowActionMenu(props: Readonly<LOSListRowActionMenuProps>) {
  const { losId, status, questionCount = 0, onEdit, onDelete, onDisable } = props;
  const { permissions } = usePermission();

  const options = useMemo<LOSListRowActionMenuOption[]>(
    () => [
      {
        icon: <EditOutlinedIcon color='action' />,
        label: 'Edit LOS',
        'data-testid': LOS_DATA_TEST_ID.ROW_ACTION_EDIT,
        onClick: () => {
          onEdit?.(losId);
        },
        requiredPermission: EDIT_LOS_REQUIRED_PERMISSIONS,
        requiredStatuses: [
          LearningObjectiveStatementStatus.Draft,
          LearningObjectiveStatementStatus.Published,
        ],
      },
      {
        icon: <DeleteOutlineIcon color='error' />,
        label: 'Delete LOS',
        'data-testid': LOS_DATA_TEST_ID.ROW_ACTION_DELETE,
        onClick: () => {
          onDelete?.(losId);
        },
        requiredPermission: DELETE_LOS_REQUIRED_PERMISSIONS,
        requiredStatuses: [LearningObjectiveStatementStatus.Draft],
        sx: (theme) => ({ color: theme.palette.error.main }),
      },
      {
        icon: <DoDisturbAltOutlinedIcon color='action' />,
        label: 'Disable LOS',
        'data-testid': LOS_DATA_TEST_ID.ROW_ACTION_DISABLE,
        onClick: () => {
          onDisable?.(losId);
        },
        disabled: questionCount > 0,
        tooltip:
          questionCount > 0 ? (
            <Text variant='captionSemibold'>
              ไม่สามารถ Disable LOS ได้
              <br />
              เนื่องจากมีข้อสอบใช้งาน LOS นี้อยู่
            </Text>
          ) : undefined,
        requiredPermission: EDIT_LOS_REQUIRED_PERMISSIONS,
        requiredStatuses: [LearningObjectiveStatementStatus.Published],
      },
    ],
    [losId, questionCount, onEdit, onDelete, onDisable],
  );

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.requiredPermission.every(
        (requiredPermission) =>
          permissions.includes(requiredPermission) &&
          (option.requiredStatuses ? option.requiredStatuses.includes(status) : true),
      ),
    );
  }, [status, options, permissions]);

  if (filteredOptions.length === 0) {
    return null;
  }
  return (
    <MoreMenuIconButton
      // TODO: implement e2e test for los action
      trigger={{ id: `los-row-action-menu-${losId}`, icon: <MoreVertIcon /> }}
      options={filteredOptions}
      slotProps={{
        iconButton: {
          'aria-label': 'los-row-more-action',
          sx: (theme) => ({
            color: theme.palette.grey[500],
            py: theme.spacing(1.5),
            px: theme.spacing(2),
          }),
        },
      }}
    />
  );
}
